import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Solution } from '../data/solutionsData';

interface SolutionCardProps {
    solution: Solution;
    index: number;
}

const SolutionCard: React.FC<SolutionCardProps> = ({ solution, index }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [markdownContent, setMarkdownContent] = useState<string>('');
    const cardRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate(); // Added useNavigate hook

    // Determine if this card should be reversed (alternating pattern)
    const isReversed = index % 2 !== 0;

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.2,
                rootMargin: '0px 0px -100px 0px'
            }
        );

        const currentRef = cardRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    useEffect(() => {
        const fetchMarkdown = async () => {
            try {
                const response = await fetch(`/data/solutions/${solution.id}.md`);
                if (response.ok) {
                    const text = await response.text();
                    setMarkdownContent(text);
                } else {
                    console.error(`Failed to load markdown for ${solution.id}`);
                    setMarkdownContent('Description not available.');
                }
            } catch (error) {
                console.error(`Error loading markdown for ${solution.id}:`, error);
                setMarkdownContent('Error loading description.');
            }
        };

        fetchMarkdown();
    }, [solution.id]);

    const contentParts = markdownContent.split('<!-- MORE -->');
    const shortText = contentParts[0];

    return (
        <div
            ref={cardRef}
            className="solution-section"
            style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
                transitionDelay: '0.2s'
            }}
        >
            <div className="container py-3">
                <div className={`row align-items-center justify-content-between gx-4 ${isReversed ? 'flex-row-reverse' : ''}`}>
                    {/* Image Column */}
                    <div className="col-lg-5 mb-3 mb-lg-0">
                        <div
                            className="solution-image-wrapper"
                            style={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '20px',
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                                transform: isVisible ? 'scale(1)' : 'scale(0.95)',
                                transition: 'transform 0.8s ease-out',
                                transitionDelay: '0.4s'
                            }}
                        >
                            <div style={{
                                position: 'relative',
                                paddingBottom: '66.67%', // 3:2 aspect ratio
                                overflow: 'hidden'
                            }}>
                                <img
                                    src={solution.imagePath}
                                    alt={solution.title}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'transform 0.5s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                />
                            </div>
                            {/* Gradient Overlay */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(0, 123, 255, 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)',
                                pointerEvents: 'none'
                            }} />
                        </div>
                    </div>

                    {/* Content Column */}
                    <div className="col-lg-6">
                        <div
                            className="solution-content"
                            style={{
                                transform: isVisible ? 'translateX(0)' : `translateX(${isReversed ? '20px' : '-20px'})`,
                                opacity: isVisible ? 1 : 0,
                                transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
                                transitionDelay: '0.6s'
                            }}
                        >
                            <div
                                style={{
                                    color: '#D5D6CC',
                                    lineHeight: '1.7'
                                }}
                            >
                                <h2 style={{
                                    fontSize: 'clamp(1.25rem, 3.5vw, 1.6rem)',
                                    fontWeight: '700',
                                    color: '#EFEFE3',
                                    lineHeight: '1.2',
                                    marginBottom: '1rem'
                                }}>{solution.title}</h2>

                                <ReactMarkdown
                                    components={{
                                        p: ({ node, ...props }) => <p style={{
                                            fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                            marginBottom: '0.8rem'
                                        }} {...props} />,
                                        ul: ({ node, ...props }) => <ul style={{
                                            paddingLeft: '1.5rem',
                                            marginBottom: '1rem'
                                        }} {...props} />,
                                        li: ({ node, ...props }) => <li style={{
                                            marginBottom: '0.5rem'
                                        }} {...props} />
                                    }}
                                >
                                    {shortText}
                                </ReactMarkdown>

                                <div style={{ textAlign: 'right', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                    <button
                                        onClick={() => navigate(`/solutions/${solution.id}`)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#18C3BA',
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            padding: '0.5rem 1rem',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            transition: 'all 0.3s ease'
                                        }}
                                        className="learn-more-btn"
                                    >
                                        Learn more...
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            style={{
                                                transition: 'transform 0.3s ease'
                                            }}
                                        >
                                            <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SolutionCard;

